import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import moment from "moment";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import InfoIcon from "@material-ui/icons/Info";
import Tooltip from "@material-ui/core/Tooltip";
import SuperLedgerPaymentStatus from "../../components/superPay/SuperLedgerPaymentStatus";

import currencyHelper from '../../helper/currency';
import ResponsiveTable from "../../components/table/ResponsiveTable";
import { ProfileType, SuperLedgerStatus, BookingType } from "../../types/enum";
import { legalEntityHasSuper } from "../../helper/legalEntityHelper";
import MultiPayeePaymentModal from "./MultiPayeePaymentModal";


const ENABLE_XERO = process.env.REACT_APP_ENABLE_XERO === "true";

const STATUS_OPTIONS = [
    { value: "", label: "All Statuses" },
    { value: SuperLedgerStatus.Pending.ordinal, label: "Pending" },
    { value: SuperLedgerStatus.Processing.ordinal, label: "Processing" },
    { value: SuperLedgerStatus.Posted.ordinal, label: "Posted" },
    { value: SuperLedgerStatus.Paid.ordinal, label: "Paid" },
    { value: SuperLedgerStatus.Failed.ordinal, label: "Failed" },
    { value: SuperLedgerStatus.Refunded.ordinal, label: "Refunded" },
];

@inject("profileStore", "superLedgerStore", "templateStore", "reportStore", "hgBillingStore", "staffFilterStore", "hgSuperPayStore", "financialProfileStore")
@withRouter
@observer
class IncomingLedgers extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedRows: [],
            showPaymentModal: false,
            selectedPayeesForPayment: [],
            actionMenuAnchorEl: null,
            actionMenuLedger: null,
            actionMenuGroup: null,
            superPayReadyFilter: "",
            readyToPayFilter: ""
        };
    }

    componentDidMount() {
        const { currentProfile } = this.props.profileStore;
        if (currentProfile?.id) {
            this.props.hgSuperPayStore.getEmployerStatus(currentProfile.id);
        }
    }

    componentDidUpdate(prevProps) {
        const { currentProfile } = this.props.profileStore;
        const isStaff = currentProfile?.type === ProfileType.Staff.ordinal;

        if (isStaff) {
            if (prevProps.staffFilterStore.filterType !== this.props.staffFilterStore.filterType) {
                this.setState({ selectedRows: [] });
            }
        } else {
            if (prevProps.reportStore.filterType !== this.props.reportStore.filterType) {
                this.setState({ selectedRows: [] });
            }
        }
    }

    handleViewItems = (ledger) => {
        const { onOpenReport } = this.props;
        if (onOpenReport) {
            onOpenReport([ledger], "incoming");
        }
    };

    handleSelectionChange = (checkedRows) => {
        this.setState({ selectedRows: checkedRows });
    };

    handleOpenPaymentModal = () => {
        const { selectedRows } = this.state;
        const { incomingLedgers, isGrouped } = this.props.superLedgerStore;

        if (isGrouped) {
            const payeeGroups = selectedRows
                .map(row => {
                    const group = row._group;
                    if (!group) return null;
                    return {
                        payeeId: group.payeeId,
                        payee: group.payee,
                        items: group.items || []
                    };
                })
                .filter(g => g && g.items.length > 0);
            this.setState({ showPaymentModal: true, selectedPayeesForPayment: payeeGroups });
            return;
        }

        const selectedIds = new Set(selectedRows.map(row => row.id));
        const payeeMap = new Map();

        incomingLedgers.forEach(ledger => {
            if (selectedIds.has(ledger.id)) {
                const payeeId = ledger.payeeId;
                if (!payeeMap.has(payeeId)) {
                    payeeMap.set(payeeId, { payeeId, payee: ledger.payee, items: [] });
                }
                payeeMap.get(payeeId).items.push(ledger);
            }
        });

        this.setState({
            showPaymentModal: true,
            selectedPayeesForPayment: Array.from(payeeMap.values())
        });
    };

    handleClosePaymentModal = () => {
        this.setState({
            showPaymentModal: false,
            selectedPayeesForPayment: []
        });
    };

    handlePaymentSuccess = () => {
        this.setState({
            showPaymentModal: false,
            selectedPayeesForPayment: [],
            selectedRows: []
        });
        this.props.templateStore.openSnackbar("Payment processed successfully");

        const { currentProfile } = this.props.profileStore;
        if (currentProfile?.id) {
            const { superLedgerStore } = this.props;
            const { isGrouped } = superLedgerStore;
            superLedgerStore.resetLedgerPage();
            if (isGrouped) {
                superLedgerStore.getGroupedLedgersByPayerId(currentProfile.id);
            } else {
                superLedgerStore.getLedgersByPayerId(currentProfile.id, 1);
            }
        }
    };

    handlePaySingleContact = (ledger) => {
        const payeeGroup = {
            payeeId: ledger.payeeId,
            payee: ledger.payee,
            items: [ledger]
        };
        this.setState({
            showPaymentModal: true,
            selectedPayeesForPayment: [payeeGroup],
            actionMenuAnchorEl: null,
            actionMenuLedger: null
        });
    };

    handleActionMenuOpen = (event, ledger, group) => {
        event.stopPropagation();
        this.setState({
            actionMenuAnchorEl: event.currentTarget,
            actionMenuLedger: ledger || null,
            actionMenuGroup: group || null
        });
    };

    handleActionMenuClose = () => {
        this.setState({
            actionMenuAnchorEl: null,
            actionMenuLedger: null,
            actionMenuGroup: null
        });
    };

    handleViewFromMenu = () => {
        const { actionMenuLedger, actionMenuGroup } = this.state;
        this.handleActionMenuClose();
        if (actionMenuGroup) {
            const items = actionMenuGroup.items || [];
            if (items.length > 0 && this.props.onOpenReport) {
                this.props.onOpenReport(items, "incoming");
            }
        } else if (actionMenuLedger) {
            this.handleViewItems(actionMenuLedger);
        }
    };

    handlePayFromMenu = () => {
        const { actionMenuLedger, actionMenuGroup } = this.state;
        if (actionMenuGroup) {
            const payeeGroup = {
                payeeId: actionMenuGroup.payeeId,
                payee: actionMenuGroup.payee,
                items: actionMenuGroup.items || []
            };
            this.setState({
                showPaymentModal: true,
                selectedPayeesForPayment: [payeeGroup],
                actionMenuAnchorEl: null,
                actionMenuLedger: null,
                actionMenuGroup: null
            });
        } else if (actionMenuLedger) {
            this.handlePaySingleContact(actionMenuLedger);
        } else {
            this.handleActionMenuClose();
        }
    };

    handleLoadMore = () => {
        const { currentProfile } = this.props.profileStore;
        const { incomingPage } = this.props.superLedgerStore;
        this.props.superLedgerStore.getLedgersByPayerId(currentProfile.id, incomingPage + 1);
    };

    handleStatusFilterChange = (event) => {
        const value = event.target.value;
        const { currentProfile } = this.props.profileStore;
        this.props.superLedgerStore.setStatusFilter(value === "" ? undefined : value);
        this.props.superLedgerStore.resetLedgerPage();
        if (currentProfile?.id) {
            const { isGrouped } = this.props.superLedgerStore;
            if (isGrouped) {
                this.props.superLedgerStore.getGroupedLedgersByPayerId(currentProfile.id);
            } else {
                this.props.superLedgerStore.getLedgersByPayerId(currentProfile.id, 1);
            }
        }
    };

    handleGroupToggle = (event) => {
        const grouped = event.target.checked;
        const { currentProfile } = this.props.profileStore;
        this.props.superLedgerStore.setIsGrouped(grouped);
        this.props.superLedgerStore.resetLedgerPage();
        this.props.superLedgerStore.setIsGrouped(grouped);
        if (currentProfile?.id) {
            if (grouped) {
                this.props.superLedgerStore.getGroupedLedgersByPayerId(currentProfile.id);
            } else {
                this.props.superLedgerStore.getLedgersByPayerId(currentProfile.id, 1);
            }
        }
        this.setState({ selectedRows: [] });
    };

    getColumns = (isStaff, isGrouped, isVenue) => {
        if (isGrouped) {
            return [
                { key: "contactName", label: "Payee Contact Name", sortable: true, width: "24%" },
                { key: "superFee", label: "Total Super Amount", sortable: true, width: "16%" },
                { key: "ledgerCount", label: "# Ledgers", width: "10%" },
                { key: "superDetails", label: "Super Pay Ready", width: "12%", align: "center" },
                { key: "readyToPay", label: "Ready to Pay", width: "12%", align: "center" },
                { key: "status", label: "Status", width: "16%" },
                { key: "actions", label: "Actions", width: "10%" }
            ];
        }
        if (isStaff) {
            return [
                { key: "contactName", label: "Payee Contact Name", sortable: true, width: "14%" },
                { key: "booking", label: "Booking", width: "34%" },
                { key: "invoiceNo", label: "Invoice #", width: "9%" },
                { key: "superFee", label: "Super Amount", sortable: true, width: "10%" },
                { key: "superDetails", label: "Super Pay Ready", width: "9%", align: "center" },
                { key: "status", label: "Status", width: "14%" },
                { key: "actions", label: "Actions", width: "6%" }
            ];
        }
        if (isVenue) {
            return [
                { key: "contactName", label: "Payee Contact Name", sortable: true, width: "14%" },
                { key: "booking", label: "Booking", width: "35%" },
                { key: "invoiceNo", label: "Invoice #", width: "8%" },
                { key: "superFee", label: "Super Amount", sortable: true, width: "10%" },
                { key: "superDetails", label: "Super Pay Ready", width: "7%", align: "center" },
                { key: "status", label: "Status", width: "12%" },
                { key: "actions", label: "Actions", width: "5%" }
            ];
        }
        return [
            { key: "contactName", label: "Payee Contact Name", sortable: true, width: "14%" },
            { key: "booking", label: "Booking", width: "28%" },
            { key: "invoiceNo", label: "Invoice #", width: "8%" },
            { key: "superFee", label: "Super Amount", sortable: true, width: "10%" },
            { key: "superDetails", label: "Super Pay Ready", width: "7%", align: "center" },
            { key: "readyToPay", label: "Ready to Pay", width: "9%", align: "center" },
            { key: "status", label: "Status", width: "10%" },
            { key: "actions", label: "Actions", width: "5%" }
        ];
    };

    getCurrencySymbol = () => {
        const { currentProfile } = this.props.profileStore;
        let currencySymbol = "$";
        if ((currentProfile?.type === ProfileType.Musician.ordinal || currentProfile?.type === ProfileType.Agent.ordinal) && currentProfile?.currency) {
            currencySymbol = currentProfile?.currency === "GBP" ? "£" : "$";
        } else if (currentProfile?.type === ProfileType.Staff.ordinal) {
            if (currentProfile.hasOrganisation) {
                currencySymbol = currentProfile.organisation[0]?.currency === "GBP" ? "£" : "$";
            } else {
                currencySymbol = currentProfile.venue?.currency === "GBP" ? "£" : "$";
            }
        }
        return currencySymbol;
    };

    renderStatusChip = (status) => {
        return <SuperLedgerPaymentStatus status={status} />;
    };

    renderFooterRow = (ledgers, currencySymbol, isGrouped) => {
        if (isGrouped) {
            const totalSuper = ledgers.reduce((sum, g) => sum + parseFloat(g.totalSuper || 0), 0);
            const totalLedgers = ledgers.reduce((sum, g) => sum + (g.items ? g.items.length : 0), 0);
            return {
                contactName: `Total (${ledgers.length} Artists, ${totalLedgers} Payments)`,
                superFee: currencySymbol + currencyHelper.formatMoney(totalSuper, 2),
                ledgerCount: "",
                superDetails: "",
                readyToPay: "",
                status: "",
                actions: ""
            };
        }
        let totalSuper = 0;
        ledgers.forEach(ledger => {
            totalSuper += parseFloat(ledger.superFee || 0);
        });
        return {
            contactName: `Total (${ledgers.length} items)`,
            booking: "",
            invoiceNo: "",
            superFee: currencySymbol + currencyHelper.formatMoney(totalSuper, 2),
            superDetails: "",
            readyToPay: "",
            status: "",
            actions: ""
        };
    };

    render() {
        const { incomingLedgers, showMoreIncoming, isGrouped, statusFilter } = this.props.superLedgerStore;
        const { selectedRows, showPaymentModal, selectedPayeesForPayment, actionMenuAnchorEl, superPayReadyFilter, readyToPayFilter } = this.state;
        const currencySymbol = this.getCurrencySymbol();
        const { currentProfile } = this.props.profileStore;
        const isStaff = currentProfile?.type === ProfileType.Staff.ordinal;
        const isVenue = currentProfile?.type === ProfileType.Venue.ordinal;
        const columns = this.getColumns(isStaff, isGrouped, isVenue);
        const isStaffNotOrgOwner = currentProfile?.hasOrganisation && !currentProfile?.isOwnOrganization;
        const activateXeroAccess = isStaffNotOrgOwner
            ? currentProfile?.organisationOwner?.activateXeroAccess
            : currentProfile?.activateXeroAccess;
        const xeroTenantId = isStaffNotOrgOwner
            ? currentProfile?.organisationOwner?.xeroTenantId
            : currentProfile?.xeroTenantId;

        const hasActiveXero = ENABLE_XERO &&
            this.props.hgBillingStore.billingAccess?.isProPlan &&
            activateXeroAccess &&
            xeroTenantId &&
            ((currentProfile?.type !== ProfileType.Staff.ordinal ||
              !currentProfile?.hasOrganisation ||
              currentProfile?.isOwnOrganization) ||
             (!currentProfile?.isOwnOrganization && currentProfile?.allowInvoices));

        const isSingleVenueStaff = isStaff && !currentProfile?.hasOrganisation;
        const isOrgStaff = isStaff && currentProfile?.hasOrganisation;
        // Org/venue-group staff always pay super via HG Super Pay (type 2), never Xero.
        // Short-circuit before consulting financialProfileStore or currentProfile.paymentType
        // so the org owner's Xero setting cannot bleed through to org staff ledgers.
                    const venueFilter = this.props.staffFilterStore.venueFilter;
                    const isLevel2Org = currentProfile?.organisation?.[0]?.hgSuperPaySetupLevel === 2;
                    const hasVenueFilter = venueFilter && venueFilter !== 0;
                    const isEligibleToPaySuperHasOrganisation = 
         ((currentProfile?.isOwnOrganization) 
                    || (!currentProfile?.isOwnOrganization && currentProfile?.allowInvoices && isLevel2Org)) 
                    && (!isLevel2Org || hasVenueFilter)
            

        const isEligibleToPaySuperNoOrganisation = (currentProfile?.venue?.userId === currentProfile?.userId)
                      && currentProfile?.venue?.paymentType === 2

        const isEligibleToPaySuper = currentProfile.allowInvoices === true
            || (!currentProfile.hasOrganisation && isEligibleToPaySuperNoOrganisation)
            || (currentProfile.hasOrganisation && isEligibleToPaySuperHasOrganisation);


        const paymentType = isOrgStaff ? 2 :
            (this.props.financialProfileStore.paymentType ??
            (isSingleVenueStaff ? (currentProfile?.venue?.paymentType ?? null) : (currentProfile?.paymentType ?? null)) ??
            null);

        const filterType = isStaff ? this.props.staffFilterStore.filterType : this.props.reportStore.filterType;
        const filterStartDate = isStaff ? this.props.staffFilterStore.startDate : this.props.reportStore.startDate;
        const filterEndDate = isStaff ? this.props.staffFilterStore.endDate : this.props.reportStore.endDate;
        const isCustomWeek = filterType === "custom" &&
            filterStartDate && filterEndDate &&
            moment(filterStartDate).isoWeekday() === 1 &&
            moment(filterEndDate).isoWeekday() === 7 &&
            moment(filterEndDate).diff(moment(filterStartDate), "days") === 6;
        const isXeroEligibleFilter = filterType === "week" || filterType === "lastweek" ||
            filterType === "quarterly" || isCustomWeek;


        let customActions = null;
        if (selectedRows.length > 0) {
            if (paymentType === 1 && hasActiveXero && isXeroEligibleFilter) {
                customActions = (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={this.handleOpenPaymentModal}
                        style={{ marginLeft: 8 }}
                    >
                        Send to XERO
                    </Button>
                );
            } else if ((currentProfile?.type === ProfileType.Staff.ordinal && isEligibleToPaySuper) || (currentProfile?.type != ProfileType.Staff.ordinal && paymentType === 2)) {
                customActions = (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={this.handleOpenPaymentModal}
                        style={{ marginLeft: 8 }}
                    >
                        Pay Super
                    </Button>
                );
            }
        }

        const itemIsReadyToPay = (i) => {
            if (i.isReadyToPay !== undefined && i.isReadyToPay !== null) return !!i.isReadyToPay;
            const isSuperComplete = paymentType === 1 ? i.isSuperAnnuationCompleteXero : i.isSuperAnnuationComplete;
            const invoice = i.invoice || {};
            const isInvoicePaid = invoice.status === "paid" || !!i.isInvoicePaid;
            return !!(isInvoicePaid && isSuperComplete);
        };

        let displayedLedgers = incomingLedgers;
        if (isGrouped) {
            const needsItemFilter = superPayReadyFilter || readyToPayFilter;
            if (needsItemFilter) {
                displayedLedgers = displayedLedgers.map(group => {
                    let items = group.items || [];
                    if (superPayReadyFilter === "ready") {
                        items = items.filter(i =>
                            paymentType === 1 ? i.isSuperAnnuationCompleteXero : i.isSuperAnnuationComplete
                        );
                    } else if (superPayReadyFilter === "not-ready") {
                        items = items.filter(i =>
                            !(paymentType === 1 ? i.isSuperAnnuationCompleteXero : i.isSuperAnnuationComplete)
                        );
                    }
                    if (readyToPayFilter === "ready") {
                        items = items.filter(i => itemIsReadyToPay(i));
                    } else if (readyToPayFilter === "not-ready") {
                        items = items.filter(i => !itemIsReadyToPay(i));
                    }
                    return { ...group, items };
                }).filter(group => (group.items || []).length > 0);
            }
        } else {
            if (superPayReadyFilter === "ready") {
                displayedLedgers = displayedLedgers.filter(l =>
                    paymentType === 1 ? l.isSuperAnnuationCompleteXero : l.isSuperAnnuationComplete
                );
            } else if (superPayReadyFilter === "not-ready") {
                displayedLedgers = displayedLedgers.filter(l =>
                    !(paymentType === 1 ? l.isSuperAnnuationCompleteXero : l.isSuperAnnuationComplete)
                );
            }
            if (readyToPayFilter === "ready") {
                displayedLedgers = displayedLedgers.filter(l => l.isReadyToPay);
            } else if (readyToPayFilter === "not-ready") {
                displayedLedgers = displayedLedgers.filter(l => !l.isReadyToPay);
            }
        }

        let mappedRows;
        if (isGrouped) {
            mappedRows = displayedLedgers.map((group) => {
                if (!group) return null;
                const payee = group.payee || {};
                const contactDisplayName = payee.profileName
                    ? payee.profileName
                    : `${payee.firstName || ''} ${payee.lastName || ''}`.trim() || 'Unknown';

                const totalFee = parseFloat(group.totalSuper || 0);
                const count = group.items ? group.items.length : 0;

                const statusDisplay = <SuperLedgerPaymentStatus group={group} />;

                const items = group.items || [];
                let superDetailsDisplay;
                if (items.length === 0) {
                    superDetailsDisplay = "—";
                } else {
                    const readyCount = items.filter(i =>
                        paymentType === 1 ? i.isSuperAnnuationCompleteXero : i.isSuperAnnuationComplete
                    ).length;
                    if (readyCount === items.length) {
                        superDetailsDisplay = (
                            <Tooltip title="All super details complete">
                                <CheckCircleIcon style={{ color: "#4CAF50" }} />
                            </Tooltip>
                        );
                    } else if (readyCount === 0) {
                        superDetailsDisplay = (
                            <Tooltip title="No super details complete">
                                <WarningIcon style={{ color: "#ff9800" }} />
                            </Tooltip>
                        );
                    } else {
                        superDetailsDisplay = (
                            <Tooltip title={`${readyCount} of ${items.length} super details complete`}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: "#f57c00" }}>{readyCount}/{items.length}</span>
                            </Tooltip>
                        );
                    }
                }

                let readyToPayDisplay;
                if (items.length === 0) {
                    readyToPayDisplay = "—";
                } else {
                    const rtpCount = items.filter(i => itemIsReadyToPay(i)).length;
                    if (rtpCount === items.length) {
                        readyToPayDisplay = (
                            <Tooltip title="All ledgers ready to pay">
                                <CheckCircleIcon style={{ color: "#4CAF50" }} />
                            </Tooltip>
                        );
                    } else if (rtpCount === 0) {
                        readyToPayDisplay = (
                            <Tooltip title="No ledgers ready to pay">
                                <WarningIcon style={{ color: "#ff9800" }} />
                            </Tooltip>
                        );
                    } else {
                        readyToPayDisplay = (
                            <Tooltip title={`${rtpCount} of ${items.length} ledgers ready to pay`}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: "#f57c00" }}>{rtpCount}/{items.length}</span>
                            </Tooltip>
                        );
                    }
                }

                return {
                    id: group.payeeId || group.id,
                    _group: group,
                    contactName: contactDisplayName,
                    superFee: currencySymbol + currencyHelper.formatMoney(totalFee, 2),
                    ledgerCount: count,
                    superDetails: superDetailsDisplay,
                    readyToPay: readyToPayDisplay,
                    status: statusDisplay,
                    actions: (
                        <IconButton size="small" aria-label="actions" onClick={(e) => this.handleActionMenuOpen(e, null, group)}>
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    )
                };
            }).filter(r => r !== null);
        } else {
            mappedRows = displayedLedgers.map((ledger) => {
                if (!ledger || !ledger.payeeId) return null;

                const row = {};
                row.id = ledger.id;

                const payee = ledger.payee || {};
                const contactDisplayName = payee.profileName
                    ? payee.profileName
                    : `${payee.firstName || ''} ${payee.lastName || ''}`.trim() || 'Unknown';

                const isNotSoleTrader = payee.legalEntity !== undefined && !legalEntityHasSuper(payee.legalEntity);
                row.contactName = isNotSoleTrader ? (
                    <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {contactDisplayName}
                        <Tooltip title="This payee is no longer a sole trader and will need to be added to Xero manually">
                            <WarningIcon style={{ color: "#ff9800", fontSize: "18px" }} />
                        </Tooltip>
                    </span>
                ) : contactDisplayName;

                const booking = ledger.booking || {};
                const bookingDate = booking.dateStart && moment.utc(booking.dateStart).isValid()
                    ? moment.utc(booking.dateStart).format("DD MMM YYYY")
                    : null;
                const bookingTitle = booking.title || booking.name || null;
                const bookingId = booking.id || null;
                const bookingLabel = [bookingDate, bookingTitle].filter(Boolean).join(" - ") || '-';
                const bookingPath = booking.type === BookingType.Adhoc.ordinal
                    ? `/privateBooking/${bookingId}/edit`
                    : `/booking/${bookingId}/details`;
                row.booking = bookingId ? (
                    <a
                        href={bookingPath}
                        style={{ color: "#1976d2", textDecoration: "none" }}
                        onClick={e => e.stopPropagation()}
                    >
                        {bookingLabel}
                    </a>
                ) : bookingLabel;

                const invoice = ledger.invoice || {};
                row.invoiceNo = invoice.no ? `HG-${invoice.no}` : '-';

                row.superFee = currencySymbol + currencyHelper.formatMoney(parseFloat(ledger.superFee || 0), 2);

                const isSuperComplete = paymentType === 1
                    ? ledger.isSuperAnnuationCompleteXero
                    : ledger.isSuperAnnuationComplete;
                const isHgSuperPayActive = paymentType === 2 && !!this.props.hgBillingStore.billingAccess?.hasActivePaymentSubscription;
                row.superDetails = isSuperComplete ? (
                    <Tooltip title="Super details added — ready to pay">
                        <CheckCircleIcon style={{ color: "#4CAF50" }} />
                    </Tooltip>
                ) : (
                        <Tooltip title={ledger.mvrErrorCode === "EMPLOYEE_NOT_SYNCED" ? "Employee Not Yet Registered" : isHgSuperPayActive ? "Not ready to pay" : paymentType === 1 ? "Artist not yet set up in Xero" : "Super details not yet added on this artist's profile"}>
                        <WarningIcon style={{ color: "#ff9800" }} />
                    </Tooltip>
                );

                const isInvoicePaid = invoice.status === "paid" || !!ledger.isInvoicePaid;
                const notReadyReason = !isInvoicePaid
                    ? "Related outgoing invoice not yet paid"
                    : !isSuperComplete
                        ? paymentType === 1 ? "Artist not yet set up in Xero" : "Super details not yet added on this artist's profile"
                        : "Not ready to pay";
                row.readyToPay = ledger.isReadyToPay ? (
                    <Tooltip title="Venue invoice is paid and super details are complete — ready to pay">
                        <CheckCircleIcon style={{ color: "#4CAF50" }} />
                    </Tooltip>
                ) : (
                    <Tooltip title={notReadyReason}>
                        <WarningIcon style={{ color: "#ff9800" }} />
                    </Tooltip>
                );

                row.status = this.renderStatusChip(ledger.status);

                row.actions = (
                    <IconButton
                        size="small"
                        onClick={(e) => this.handleActionMenuOpen(e, ledger)}
                        aria-label="actions"
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                );

                return row;
            }).filter(row => row !== null);
        }

        const { actionMenuLedger } = this.state;

        return (
            <Fragment>
                <Grid container spacing={8}>
                    <Grid item xs={12} lg={12}>
                        <div>
                            <Typography variant="headline" component="h3" gutterBottom>
                                Super I Owe
                            </Typography>
                            <Typography gutterBottom className="side-helper-text">
                                Super you're responsible to remit when you are the payer for a sole trader artist on a booking
                            </Typography>
                        </div>
                    </Grid>
                    <Grid item xs={12} lg={12}>
                        <Grid container spacing={16}>
                            <Grid item xs={12}>
                                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
                                    <FormControl style={{ minWidth: 180 }}>
                                        <InputLabel shrink htmlFor="incoming-status-filter">
                                            Filter by Status&nbsp;
                                            <Tooltip title="Filter ledgers by their payment status — Pending, Processing, Posted, Paid, Failed, or Refunded.">
                                                <InfoIcon style={{ fontSize: 14, verticalAlign: "middle", color: "#9e9e9e", cursor: "help" }} />
                                            </Tooltip>
                                        </InputLabel>
                                        <Select
                                            displayEmpty
                                            value={statusFilter === undefined || statusFilter === null ? "" : statusFilter}
                                            onChange={this.handleStatusFilterChange}
                                            inputProps={{ id: "incoming-status-filter" }}
                                        >
                                            {STATUS_OPTIONS.map(opt => (
                                                <MenuItem key={String(opt.value)} value={opt.value}>{opt.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl style={{ minWidth: 180 }}>
                                        <InputLabel shrink htmlFor="incoming-super-ready-filter">
                                            Super Pay Ready&nbsp;
                                            <Tooltip title="Filter by whether the payee's superannuation fund details (fund name, ABN, USI, member number) are set up and ready for payment.">
                                                <InfoIcon style={{ fontSize: 14, verticalAlign: "middle", color: "#9e9e9e", cursor: "help" }} />
                                            </Tooltip>
                                        </InputLabel>
                                        <Select
                                            displayEmpty
                                            value={superPayReadyFilter}
                                            onChange={e => this.setState({ superPayReadyFilter: e.target.value, selectedRows: [] })}
                                            inputProps={{ id: "incoming-super-ready-filter" }}
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="ready">Ready</MenuItem>
                                            <MenuItem value="not-ready">Not Ready</MenuItem>
                                        </Select>
                                    </FormControl>
                                    {!isStaff && !isVenue && (
                                    <FormControl style={{ minWidth: 180 }}>
                                        <InputLabel shrink htmlFor="incoming-ready-to-pay-filter">
                                            Ready to Pay&nbsp;
                                            <Tooltip title="Filter by whether all conditions are met to process this super payment — the related invoice must be paid and super details must be complete.">
                                                <InfoIcon style={{ fontSize: 14, verticalAlign: "middle", color: "#9e9e9e", cursor: "help" }} />
                                            </Tooltip>
                                        </InputLabel>
                                        <Select
                                            displayEmpty
                                            value={readyToPayFilter}
                                            onChange={e => this.setState({ readyToPayFilter: e.target.value, selectedRows: [] })}
                                            inputProps={{ id: "incoming-ready-to-pay-filter" }}
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="ready">Ready</MenuItem>
                                            <MenuItem value="not-ready">Not Ready</MenuItem>
                                        </Select>
                                    </FormControl>
                                    )}
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={isGrouped}
                                                onChange={this.handleGroupToggle}
                                                color="primary"
                                            />
                                        }
                                        label="Group by Payee"
                                    />
                                </div>
                                <Paper>
                                    <div style={{ overflowX: "auto" }}>
                                        <ResponsiveTable
                                            showToolbar={true}
                                            selectable={true}
                                            getCheckedRows={this.handleSelectionChange}
                                            columns={columns}
                                            rows={mappedRows}
                                            sortable={true}
                                            footerRow={this.renderFooterRow(displayedLedgers, currencySymbol, isGrouped)}
                                            customActions={customActions}
                                        />
                                    </div>
                                </Paper>
                                {!isGrouped && showMoreIncoming && (
                                    <div style={{ textAlign: "center", padding: "16px" }}>
                                        <Button variant="outlined" onClick={this.handleLoadMore}>
                                            Load More
                                        </Button>
                                    </div>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                <Menu
                    anchorEl={actionMenuAnchorEl}
                    open={Boolean(actionMenuAnchorEl)}
                    onClose={this.handleActionMenuClose}
                >
                    <MenuItem onClick={this.handleViewFromMenu}>View</MenuItem>
                    {paymentType === 2 && (
                        <MenuItem onClick={this.handlePayFromMenu}>Pay</MenuItem>
                    )}
                </Menu>

                <MultiPayeePaymentModal
                    open={showPaymentModal}
                    onClose={this.handleClosePaymentModal}
                    payees={selectedPayeesForPayment}
                    currencySymbol={currencySymbol}
                    onSuccess={this.handlePaymentSuccess}
                    startDate={isStaff ? this.props.staffFilterStore.startDate : this.props.reportStore.startDate}
                    endDate={isStaff ? this.props.staffFilterStore.endDate : this.props.reportStore.endDate}
                    filterType={isStaff ? this.props.staffFilterStore.filterType : this.props.reportStore.filterType}
                    isHgSuperPay={paymentType === 2}
                    {...(paymentType === 2
                        ? { title: "Pay Super", submitLabel: "Pay Now" }
                        : {})}
                />
            </Fragment>
        );
    }
}

export default IncomingLedgers;
